import { CabinetWrapper } from '@/shared/cabinet-wrapper';
import { PageTitle } from '@/shared/page-title';
import { Box, Button, Stack, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import SearchIcon from '@/app/assets/icons/search-alt.svg?react';
import useFilters from '@/app/hooks/useFilters';
import _ from 'lodash';
import {
  useGetFactoringClientsQuery,
  useLazyGetFactoringClientsExportQuery,
} from '@/entities/didox/api';
import { EmptyDataPlaceholder } from '@/shared/ui/empty-data-placeholder';
import { UITable } from '@/shared/ui/ui-table';
import { useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getFetchEror, triada } from '@/app/helpers';
import PlusIcon from '@/app/assets/icons/plus.svg?react';
import PrinterIcon from '@mui/icons-material/Print';
import { useNotification } from '@/shared/contexts/NotificationContext';

export const FactorAvailableClientsPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { filters, updateFilters } = useFilters({
    defaultFilters: {
      page: 1,
      size: 20,
      search: '',
      sort: 'incomingInvoiceSum',
      order: 'desc',
    },
  });
  const { showNotification } = useNotification();

  const { data, isFetching } = useGetFactoringClientsQuery(filters);
  const [getFactoringClientsExport] = useLazyGetFactoringClientsExportQuery();

  const [isExportLoading, setIsExportLoading] = useState<boolean>(false);
  const [inputError, setInputError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const handleSearch = _.debounce((value: string) => {
    setInputError(null);
    updateFilters({ search: value });
  }, 300);

  const onAddManually = () => {
    const value = inputRef.current?.value;

    if (value && /^[0-9]{9,14}$/.test(value)) {
      setInputError(null);
      navigate(`/factor/limits/clients/add/${value}`);
    } else {
      setInputError(t('add_manually_client_input_error'));
    }
  };

  const handleDownload = async () => {
    try {
      setIsExportLoading(true);
      const response = await getFactoringClientsExport().unwrap();

      const url = window.URL.createObjectURL(response);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'clients.xlsx');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      setIsExportLoading(false);
    } catch (err) {
      showNotification(getFetchEror(err));
    }
  };

  return (
    <CabinetWrapper>
      <PageTitle title={t('available_clients')} />

      <Stack direction='row' spacing={2} mt={2.5} alignItems='flex-start'>
        <TextField
          placeholder={t('tin_or_organization_name')}
          onChange={(e) => handleSearch(e.target.value)}
          sx={{ flex: 1 }}
          InputProps={{
            endAdornment: <SearchIcon />,
          }}
          inputRef={inputRef}
          helperText={inputError}
          error={Boolean(inputError)}
        />
        <Box onClick={handleDownload}>
          <Button
            sx={{ height: 40 }}
            color='inherit'
            startIcon={<PrinterIcon />}
            disabled={isExportLoading}
          >
            {t('download')}
          </Button>
        </Box>
        <Button color='inherit' onClick={onAddManually} sx={{ height: 40 }}>
          {t('add_manually')}
        </Button>
      </Stack>

      <EmptyDataPlaceholder
        title={t('no_available_clients_title')}
        subtitle={t('no_available_clients_subtitle')}
        isEmpty={!data?.data?.length}
        isLoading={isFetching}
      >
        <UITable
          sx={{ mt: 2.5 }}
          data={data?.data ?? []}
          columns={[
            {
              label: t('name'),
              render: (row) => (
                <Box>
                  <Typography fontSize={13}>{row.company}</Typography>
                  <Typography fontSize={13} color='primary.main'>
                    {row.taxIdOrPinfl}
                  </Typography>
                </Box>
              ),
              sortKey: 'company',
            },
            {
              label: t('rating_gnk'),
              render: (row) =>
                row?.rating?.title &&
                row.rating?.type &&
                row.rating?.points &&
                `${row.rating?.type}-${row.rating?.points} ${row.rating?.title}`,
            },
            {
              label: t('income_sum'),
              render: (row) => triada(row.outgoingInvoiceSum),
              sortKey: 'outgoingInvoiceSum',
            },
            {
              label: t('suppliers_count'),
              render: (row) => row.countSuppliers,
              sortKey: 'countSuppliers',
            },
            {
              label: t('outgoing_invoices_sum'),
              render: (row) => triada(row.incomingInvoiceSum),
              sortKey: 'incomingInvoiceSum',
            },
            {
              label: t('actions'),
              render: (row) => (
                <Link to={`/factor/limits/clients/add/${row.taxIdOrPinfl}`}>
                  <Button startIcon={<PlusIcon />}>{t('add_limit')}</Button>
                </Link>
              ),
              nowrap: true,
            },
          ]}
          sort={{
            sortKey: filters.sort,
            sortDirection: filters.order,
            onSortChange: (sort, order) => {
              updateFilters({ sort, order });
            },
          }}
          PaginationProps={{
            count: Math.ceil((data?.total || 0) / filters.size),
            page: filters.page,
            onChange: (_, page) => updateFilters({ page }),
            sizes: {
              currentRowsPerPage: String(filters.size),
              onRowsPerPageChange: (size) =>
                updateFilters({ size: Number(size), page: 1 }),
            },
          }}
        />
      </EmptyDataPlaceholder>
    </CabinetWrapper>
  );
};
